import { useEffect, useRef } from 'react';

function AddressAutocomplete({
  value,
  onChange,
  placeholder = "Address",
  required = false,
  restrictCountry = null,
  restrictState = null,
  restrictCity = null
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    // Wait for Google Maps API to be loaded
    const initAutocomplete = () => {
      if (!window.google || !inputRef.current) return;

      // Build autocomplete options with restrictions
      const autocompleteOptions = {
        types: ['address'],
        fields: ['formatted_address', 'address_components', 'geometry'],
      };

      // Restrict to country if provided
      if (restrictCountry) {
        // Convert country name to ISO code (this is a simple mapping, expand as needed)
        const countryCodeMap = {
          'India': 'IN',
          'United States': 'US',
          'United Kingdom': 'GB',
          'Canada': 'CA',
          'Australia': 'AU',
          // Add more mappings as needed
        };
        const countryCode = countryCodeMap[restrictCountry] || restrictCountry;
        autocompleteOptions.componentRestrictions = { country: countryCode };
      }

      // Create autocomplete instance
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, autocompleteOptions);

      // If state or city is selected, add additional filtering in the listener
      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        // Only process if we have a valid place with address details
        if (!place || !place.formatted_address) {
          return; // User pressed enter or no valid selection
        }

        // Extract address components
        let country = '';
        let state = '';
        let city = '';
        let streetAddress = '';
        let postalCode = '';

        place.address_components?.forEach((component) => {
          const types = component.types;

          if (types.includes('country')) {
            country = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            state = component.long_name;
          }
          if (types.includes('locality') || types.includes('administrative_area_level_2')) {
            city = component.long_name;
          }
          if (types.includes('route') || types.includes('street_number')) {
            streetAddress += component.long_name + ' ';
          }
          if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });

        // Validate against state and city restrictions if provided
        if (restrictState && state && state !== restrictState) {
          alert(`Please select a location within ${restrictState}. The selected location is in ${state}.`);
          return;
        }

        if (restrictCity && city && city !== restrictCity) {
          alert(`Please select a location within ${restrictCity}. The selected location is in ${city}.`);
          return;
        }

        // Call onChange with the selected address
        onChange({
          fullAddress: place.formatted_address,
          country,
          state,
          city,
          streetAddress: streetAddress.trim(),
          postalCode,
          lat: place.geometry?.location?.lat(),
          lng: place.geometry?.location?.lng(),
        });
      });
    };

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      initAutocomplete();
    } else {
      // Wait for Google Maps to load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          initAutocomplete();
        }
      }, 100);

      return () => clearInterval(checkGoogleMaps);
    }
  }, [onChange, restrictCountry, restrictState, restrictCity]);

  const handleInputChange = (e) => {
    // Allow manual typing - pass the string value directly
    onChange(e.target.value);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={handleInputChange}
      autoComplete="off"
      required={required}
    />
  );
}

export default AddressAutocomplete;
